import { AppConfig } from 'config/config';
import { Series } from '@models/Series';
import { useQuery } from '@tanstack/react-query';
import { isAuthError, loadConfig } from '@api/api';
import { imageUrlToArrayBufferSimple } from 'utils/captureUtils';
import axios, { AxiosResponse } from 'axios';

export interface iImageItem {
  image: ArrayBuffer;
  name: string;
}

export interface iPACS {
  series_id: string;
  json: object;
  image_data: iImageItem[];
}

// export interface JsonData {
//   PACS_INFO: PACSInfo;
//   SendInfo: SendInfoItem[];
// }

export interface PACSInfo {
  AETITLE: string;
  HOST_URL: string;
  PORT: string;
  REMOTE_AETITLE: string;
  SEND_MODE: string;
}

export interface ChamberInfoItem {
  capture_kind: string;
  series_description: string;
}

export type ServerCaptureBlendMode = 'VR' | 'MIP';

export interface ServerCaptureTargetIndexItem {
  index: number;
  color: [number, number, number];
}

export interface ServerCaptureCameraInfo {
  position: [number, number, number];
  focalPoint: [number, number, number];
  viewUp: [number, number, number];
  parallelProjection: boolean;
  parallelScale: number;
  hasClippingRange: boolean;
  clippingRange: [number, number];
}

export interface ServerCaptureSendInfoBase {
  windowWidth: string;
  windowCenter: string;
  SliceCount: number;
  thickness: number;
  planar_spacing: number;
  vertical_spacing: number;
  screen_capture: boolean;
  series_description: string;
  newWidth: number;
  newHeight: number;
  labels: null;
  draw_description: boolean;
}

export interface ServerCaptureLightingOption {
  enabled: boolean;
  ambient: number;
}

export interface ServerCaptureSegmentationScoutImageInfo {
  targetindex: ServerCaptureTargetIndexItem[];
  IsSurfaceOn: boolean;
  IsCenterlineOn: boolean;
  BlendMode: ServerCaptureBlendMode;
  HeartCut: boolean;
  CaptureType: 0 | 1;
  Camera: ServerCaptureCameraInfo;
  LightingOption: ServerCaptureLightingOption;
}

export interface ServerCaptureSegmentationSendInfoItem
  extends ServerCaptureSendInfoBase {
  capture_kind: 'segmentation';
  ScoutImageInfo: ServerCaptureSegmentationScoutImageInfo;
}

export interface ServerCaptureCPRScoutImageInfo {
  targetindex: ServerCaptureTargetIndexItem[];
  IsSurfaceOn: boolean;
  IsCenterlineOn: boolean;
  BlendMode: ServerCaptureBlendMode;
  HeartCut: boolean;
  CaptureType: 0 | 1;
  Camera: ServerCaptureCameraInfo;
  LightingOption: ServerCaptureLightingOption;
}

export interface ServerCaptureCPRColorItem {
  selected: boolean;
  color: number;
  value: number;
}

export type ServerCaptureCPRKind = 'stretched' | 'straightened';

export interface ServerCaptureCPRModeOption {
  angleStartDegrees: number;
}

export interface ServerCaptureCPRSendInfoItem
  extends ServerCaptureSendInfoBase {
  capture_kind: ServerCaptureCPRKind | ServerCaptureCPRKind[];
  branchid: number;
  centerlineExpand: {
    cprFrontExpand: number;
    cprBackExpand: number;
  };
  stretched?: ServerCaptureCPRModeOption;
  straightened?: ServerCaptureCPRModeOption;
  Is_cpr_label_on: boolean;
  selected_color_labels: Array<'plaque' | 'lumen'>;
  color: ServerCaptureCPRColorItem[];
  ScoutImageInfo: ServerCaptureCPRScoutImageInfo;
}


export interface ServerCaptureRangeCSSendInfoItem
  extends ServerCaptureSendInfoBase {
  capture_kind: 'range_cs';
  branchid: number;
  centerlineExpand: {
    cprFrontExpand: number;
    cprBackExpand: number;
  };
  angleStartDegrees: number;
  range_cs_info: {
    thickness: number;
    spacing: number;
    proximal: number;
    distal: number;
    branch_name: string;
  };
}

export type ServerCaptureSendInfoItem =
  | ServerCaptureSegmentationSendInfoItem
  | ServerCaptureCPRSendInfoItem
  | ServerCaptureRangeCSSendInfoItem;

export interface SendInfoItem {
  windowWidth: string;
  windowCenter: string;
  SliceCount: number;
  thickness: number;
  planar_spacing: number;
  vertical_spacing: number;
  screen_capture: boolean;
  capture_kind: string;
  series_description: string;
  newWidth: number;
  newHeight: number;
  labels: LabelItem[];
  draw_description: boolean;
}

export interface CPRInfo {
  Graph_Type: string;
  length: number;
  angle: number;
  mean_diameter?: number;
  mean_area?: number;
}

export interface LabelItem {
  path: string;
  width: number;
  height: number;
  orientation: number[]; // length: 6
  position: number[]; // length: 3
  crop_size?: number;
  cpr_info?: CPRInfo;
  text?: string;
}

const getPACSApi = async (series_id: string) => {
  const BASE_URL = await loadConfig();
  return `${BASE_URL}/api/v1/series/report/${series_id}`;
};

async function make_dummy_image_data(
  SendInfo: SendInfoItem[],
): Promise<iImageItem[]> {
  const image_data: iImageItem[] = [];
  for (const value of SendInfo) {
    for (const label of value.labels) {
      const url = '/capture/' + label.path;
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();

      const image_: iImageItem = {
        image: arrayBuffer,
        name: label.path,
      };

      image_data.push(image_);
    }
  }

  return image_data;
}

function make_dummy_json() {
  const pacs_info: PACSInfo[] = [
    {
      AETITLE: 'CARDIO_LUCID',
      HOST_URL: '172.30.20.13',
      PORT: '4242',
      REMOTE_AETITLE: 'CARDIOLUCID',
      SEND_MODE: '1',
    },
    {
      AETITLE: 'CARDIO_LUCID1',
      HOST_URL: '172.30.20.131',
      PORT: '42421',
      REMOTE_AETITLE: 'CARDIOLUCID1',
      SEND_MODE: '0',
    },
  ];

  const send_info: SendInfoItem[] = [
    {
      windowWidth: '1500',
      windowCenter: '415',
      SliceCount: 12,
      thickness: 1.0,
      planar_spacing: 0.345,
      vertical_spacing: 0.345,
      screen_capture: false,
      capture_kind: '4CHAMBER',
      series_description: "I'm 4 Chamber",
      newWidth: 1024,
      newHeight: 1024,
      labels: [
        {
          path: '0.jpg',
          width: 794,
          height: 1122,
          orientation: [1.0, 0.0, 0.0, 0.0, 1.0, 0.0],
          position: [-71.3271484375, -274.3271484375, 313.4],
        },
        {
          path: '1.jpg',
          width: 6872,
          height: 6872,
          orientation: [1.0, 0.0, 0.0, 0.0, 1.0, 0.0],
          position: [-71.3271484375, -274.3271484375, 313.4],
        },
        {
          path: '2.jpg',
          width: 3740,
          height: 5236,
          orientation: [1.0, 0.0, 0.0, 0.0, 1.0, 0.0],
          position: [-71.3271484375, -274.3271484375, 313.4],
        },
        {
          path: '3.jpg',
          width: 2880,
          height: 2160,
          orientation: [1.0, 0.0, 0.0, 0.0, 1.0, 0.0],
          position: [-71.3271484375, -274.3271484375, 313.4],
        },
        {
          path: '4.jpg',
          width: 2880,
          height: 2160,
          orientation: [1.0, 0.0, 0.0, 0.0, 1.0, 0.0],
          position: [-71.3271484375, -274.3271484375, 313.4],
        },
      ],
      draw_description: false,
    },
    {
      windowWidth: '1500',
      windowCenter: '415',
      SliceCount: 12,
      thickness: 1.0,
      planar_spacing: 0.345,
      vertical_spacing: 0.345,
      screen_capture: false,
      capture_kind: 'CPR',
      series_description: "I'm CPR",
      newWidth: 1024,
      newHeight: 1024,
      labels: [
        {
          path: '5.jpg',
          width: 2880,
          height: 2160,
          orientation: [1.0, 0.0, 0.0, 0.0, 1.0, 0.0],
          position: [-71.3271484375, -274.3271484375, 313.4],
        },
        {
          path: '6.bin',
          width: 512,
          height: 512,
          orientation: [1.0, 0.0, 0.0, 0.0, 1.0, 0.0],
          position: [-71.3271484375, -274.3271484375, 313.4],
        },
        {
          path: '7.bin',
          width: 512,
          height: 512,
          orientation: [1.0, 0.0, 0.0, 0.0, 1.0, 0.0],
          position: [-71.3271484375, -274.3271484375, 313.4],
        },
        {
          path: '8.bin',
          width: 512,
          height: 512,
          orientation: [1.0, 0.0, 0.0, 0.0, 1.0, 0.0],
          position: [-71.3271484375, -274.3271484375, 313.4],
        },
        {
          path: '9.bin',
          width: 512,
          height: 512,
          orientation: [1.0, 0.0, 0.0, 0.0, 1.0, 0.0],
          position: [-71.3271484375, -274.3271484375, 313.4],
        },
      ],
      draw_description: false,
    },
  ];

  return {
    PACS_INFO: pacs_info,
    SendInfo: send_info,
  };
}

async function ArrayBufferToFormData(info: iPACS) {
  const mimeType = 'image/jpeg'; // 혹은 image/png 등

  // 2. FormData 생성
  const formData = new FormData();
  formData.append('pacs_upload', String(false));
  console.log(JSON.stringify(info.json));
  formData.append('data', JSON.stringify(info.json));

  info.image_data.forEach((item) => {
    const blob = new Blob([item.image], { type: mimeType });
    const file = new File([blob], item.name, { type: mimeType });
    formData.append('files', file);
  });
  return formData;
}

export async function send_info_to_pacs(accessToken: string, info: iPACS) {
  const url = await getPACSApi(info.series_id);

  const send_info = await ArrayBufferToFormData(info);
  return await axios({
    method: 'post',
    url: url,
    headers: {
      Accept: '*/*',
      Authorization: `Bearer ${accessToken}`,
    },
    data: send_info,
    timeout: 0,
  })
    .then((res) => {
      console.log(res);
      return res;
    })
    .catch((err) => {
      console.log(err);
      return err;
    });
}

export async function example_send_info_to_pacs(
  accessToken: string,
  series_id: string,
) {
  const json_data = make_dummy_json();
  const image_data = await make_dummy_image_data(json_data.SendInfo);

  const pacs_result = await send_info_to_pacs(accessToken, {
    series_id: series_id,
    json: json_data,
    image_data: image_data,
  });

  console.log('send pacs result: ', pacs_result);
}

export function createPACSInfo(pacsinfo: {
  AETITLE: string;
  HOST_URL: string;
  PORT: string;
  REMOTE_AETITLE: string;
  SEND_MODE: string;
}) {
  const pacs_info: PACSInfo = {
    AETITLE: pacsinfo.AETITLE,
    HOST_URL: pacsinfo.HOST_URL,
    PORT: pacsinfo.PORT,
    REMOTE_AETITLE: pacsinfo.REMOTE_AETITLE,
    SEND_MODE: pacsinfo.SEND_MODE,
  };
  return pacs_info;
}

export async function createSendInfo(
  info: {
    windowWidth: string;
    windowCenter: string;
    SliceCount: number;
    thickness: number;
    planar_spacing: number;
    vertical_spacing: number;
    screen_capture: boolean;
    capture_kind: string;
    series_description: string;
    newWidth: number;
    newHeight: number;
  },
  urls: string[],
) {
  let send_info: SendInfoItem = {
    windowWidth: info.windowWidth,
    windowCenter: info.windowCenter,
    SliceCount: info.SliceCount,
    thickness: info.thickness,
    planar_spacing: info.planar_spacing,
    vertical_spacing: info.vertical_spacing,
    screen_capture: info.screen_capture,
    capture_kind: info.capture_kind,
    series_description: info.series_description,
    newWidth: info.newWidth,
    newHeight: info.newHeight,
    labels: [],
    draw_description: false,
  };

  let image_data: iImageItem[] = [];

  for (let i = 0; i < urls.length; i++) {
    const data = await imageUrlToArrayBufferSimple(urls[i]);
    const pathName = `${i}.png`;
    send_info.labels.push({
      path: pathName,
      width: data.width,
      height: data.height,
      orientation: [1.0, 0.0, 0.0, 0.0, 1.0, 0.0],
      position: [-71.3271484375, -274.3271484375, 313.4],
    });
    const image_: iImageItem = {
      image: data.buffer,
      name: pathName,
    };
    image_data.push(image_);
  }

  return { send_info, image_data };
  // series.imageTag.windowWidth;
  // series.imageTag.windowCenter;
  // //slicecount
  // series.imageTag.sliceThickness;
  // series.imageTag.planarSpacing;
  // series.imageTag.pixelSpacing;
  // series.imageTag.planarSpacingInterpolated;

  // //screen capture
  // //capture kind
  // // description
  // //width
  // //height
}

export async function createSendJsonandData(
  pacsinfo: {
    AETITLE: string;
    HOST_URL: string;
    PORT: string;
    REMOTE_AETITLE: string;
    SEND_MODE: string;
    BASE_SERIES_ID: string;
  },
  info: {
    windowWidth: string;
    windowCenter: string;
    SliceCount: number;
    thickness: number;
    planar_spacing: number;
    vertical_spacing: number;
    screen_capture: boolean;
    capture_kind: string;
    series_description: string;
    newWidth: number;
    newHeight: number;
  },
  urls: string[],
) {
  const pacs_info = createPACSInfo(pacsinfo);
  const { send_info, image_data } = await createSendInfo(info, urls);
  const json = { PACS_INFO: [pacs_info], SendInfo: [send_info] };
  return { json, image_data };
}

export async function send_capture_image_to_pacs(
  pacsinfo: {
    AETITLE: string;
    HOST_URL: string;
    PORT: string;
    BASE_SERIES_ID: string;
    REMOTE_AETITLE: string;
    SEND_MODE: string;
  },
  info: {
    windowWidth: string;
    windowCenter: string;
    SliceCount: number;
    thickness: number;
    planar_spacing: number;
    vertical_spacing: number;
    screen_capture: boolean;
    capture_kind: string;
    series_description: string;
    newWidth: number;
    newHeight: number;
  },
  urls: string[],
  accessToken: string,
  series_id: string,
) {
  const { json, image_data } = await createSendJsonandData(
    pacsinfo,
    info,
    urls,
  );
  console.log(json);

  const pacs_result = await send_info_to_pacs(accessToken, {
    series_id,
    json,
    image_data,
  });

  return pacs_result;
}
